import React, { useEffect, useState } from "react"
import moment from "moment"
import { Button, Col, Modal, ModalBody, ModalHeader, Row } from "reactstrap"
import { useSelector } from "react-redux"
import { Carousel } from "react-responsive-carousel"
import "react-responsive-carousel/lib/styles/carousel.min.css"

import { fetchAvailableSlots, fetchAppointmentsForDateRange } from "helpers/api_helper"
import { getUserDetails } from "store/slices"
import DaySchedule from "../pages/Administration/components/DaySchedule"
import calendarIcon from "../assets/images/calendar.png"

const DAYS_TO_SHOW = 15

const isBookedSlot = slot => {
  const status = String(slot?.status || slot?.availabilityStatus || "").toUpperCase()
  return Boolean(slot?.isBooked || status === "BOOKED")
}

const normalizeSlots = slots => {
  const byTime = new Map()
  slots.forEach(slot => {
    const key = String(slot?.time || slot?.startTime || "")
    if (!key) return
    if (!byTime.has(key) || isBookedSlot(slot)) byTime.set(key, slot)
  })
  return Array.from(byTime.values()).sort((a, b) =>
    String(a.time || a.startTime).localeCompare(String(b.time || b.startTime))
  )
}

const buildDay = (date, slots = []) => ({
  _id: `dynamic-${date}`,
  date,
  day: `${date}T00:00:00`,
  isAvailable: slots.length > 0,
  startTime: slots[0]?.time || "00:00",
  endTime: slots[slots.length - 1]?.time || "23:59",
  slots: slots.map(slot => ({
    ...slot,
    _id: slot.slotId,
    timingId: slot.timingId || "dynamic",
    isBooked: isBookedSlot(slot),
    status:
      slot.available === false ||
      ["BLOCKED", "BOOKED", "UNAVAILABLE"].includes(
        String(slot.status || slot.availabilityStatus || "").toUpperCase()
      )
        ? "BLOCKED"
        : "",
  })),
})

const DoctorTimings = ({
  isOpen,
  setModal,
  onChangeDate,
  initialDate,
  refreshKey = 0,
}) => {
  document.title = "Timing | SuPaw"
  const userDetails = useSelector(getUserDetails)
  const [nextDays, setNextDays] = useState([])
  const [selectedDate, setSelectedDate] = useState(null)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState("")
  const [noSlotAvailable, setNoSlotAvailable] = useState(false)
  const [initialDateApplied, setInitialDateApplied] = useState(false)

  useEffect(() => {
    setInitialDateApplied(false)
  }, [initialDate])

  useEffect(() => {
    const loadAvailability = async () => {
      if (!isOpen || !userDetails?._id) return

      setIsLoading(true)
      setLoadError("")
      setCurrentIndex(0)

      try {
        const dates = Array.from({ length: DAYS_TO_SHOW }, (_, index) =>
          moment().startOf("day").add(index, "days").format("YYYY-MM-DD")
        )
        const [responses, bookedResponse] = await Promise.all([
          Promise.all(dates.map(date => fetchAvailableSlots({ doctorId: userDetails._id, date }))),
          fetchAppointmentsForDateRange(userDetails._id, dates[0], dates[dates.length - 1]),
        ])
        const bookedData = bookedResponse?.data
        const booked = bookedResponse?.status === 1
          ? (bookedData?.appointments || (Array.isArray(bookedData) ? bookedData : []))
          : []
        const successfulResponses = responses.filter(
          response => response?.status === 1
        )

        if (!successfulResponses.length) {
          throw new Error(
            responses[0]?.message || "Unable to load appointment availability."
          )
        }

        setNextDays(
          dates.map((date, index) => {
            const slots = normalizeSlots([
                ...(responses[index]?.status === 1 ? responses[index]?.data?.slots || [] : []),
                ...booked
                  .filter(item => moment(item.date).format("YYYY-MM-DD") === date)
                  .map(item => ({
                    time: item.time,
                    slotId: item.slotId || `booked-${item._id}`,
                    timingId: item.timingId || "dynamic",
                    available: false,
                    status: "BLOCKED",
                    isBooked: true,
                  })),
            ])
            return buildDay(date, slots)
          })
        )
      } catch (error) {
        setNextDays([])
        setLoadError(
          error?.message || "Unable to load appointment availability."
        )
      } finally {
        setIsLoading(false)
      }
    }

    loadAvailability()
  }, [isOpen, refreshKey, userDetails?._id])

  const selectSlot = (dayData, index, time) => {
    const slot = dayData.slots.find(item => item.time === time)
    if (!slot) return

    setSelectedDate({ time, dateIndex: index })
    onChangeDate({
      day: dayData.day,
      time,
      dateIndex: index,
      timingId: slot.timingId || "dynamic",
      slotId: slot.slotId || slot._id,
    })
    setModal(false)
  }

  useEffect(() => {
    if (!initialDate || initialDateApplied || !nextDays.length || isLoading) {
      return
    }

    const requested = moment(Number(initialDate))
    if (!requested.isValid()) {
      setInitialDateApplied(true)
      return
    }

    const dateKey = requested.format("YYYY-MM-DD")
    const time = requested.format("HH:mm")
    const dayIndex = nextDays.findIndex(day => day.date === dateKey)
    const slot =
      dayIndex >= 0
        ? nextDays[dayIndex].slots.find(item => item.time === time)
        : null

    setInitialDateApplied(true)
    if (slot) selectSlot(nextDays[dayIndex], dayIndex, time)
  }, [initialDate, initialDateApplied, isLoading, nextDays])

  useEffect(() => {
    if (!isOpen || !nextDays.length) {
      setNoSlotAvailable(false)
      return
    }
    setNoSlotAvailable(
      !nextDays[currentIndex]?.slots?.some(slot => slot.status !== "BLOCKED")
    )
  }, [currentIndex, isOpen, nextDays])

  const handleNavigate = direction => {
    if (direction === "prev" && currentIndex > 0) {
      setCurrentIndex(currentIndex - 1)
    } else if (direction === "next" && currentIndex < nextDays.length - 1) {
      setCurrentIndex(currentIndex + 1)
    }
  }

  return (
    <>
      <div className={noSlotAvailable && isOpen ? "blur-background" : ""}>
        <Modal
          isOpen={isOpen}
          toggle={() => setModal(previous => !previous)}
          size="lg"
        >
          <ModalHeader toggle={() => setModal(false)}>Select Time</ModalHeader>
          <ModalBody className="py-3 px-5">
            {isLoading ? (
              <Row className="justify-content-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </Row>
            ) : loadError ? (
              <Row className="justify-content-center py-4">
                <Col xs="12" className="text-center">
                  <p className="text-danger mb-3">{loadError}</p>
                  <Button color="primary" onClick={() => setModal(false)}>
                    Close
                  </Button>
                </Col>
              </Row>
            ) : nextDays.length ? (
              <Row>
                <div className="w-100">
                  <Carousel
                    selectedItem={currentIndex}
                    showArrows={false}
                    showIndicators={false}
                    showThumbs={false}
                    showStatus={false}
                    onChange={setCurrentIndex}
                  >
                    {nextDays.map((day, index) => (
                      <DaySchedule
                        isCurrentDay={index === 0}
                        key={day._id}
                        value={day}
                        difference={30}
                        isSelectOnly
                        selectedDate={
                          selectedDate?.dateIndex === index
                            ? selectedDate.time
                            : null
                        }
                        onSelectedDate={time => selectSlot(day, index, time)}
                      />
                    ))}
                  </Carousel>
                  <div className="d-flex justify-content-between mt-3">
                    <Button
                      color="primary"
                      onClick={() => handleNavigate("prev")}
                      disabled={currentIndex === 0}
                    >
                      Previous
                    </Button>
                    <Button
                      color="primary"
                      onClick={() => handleNavigate("next")}
                      disabled={currentIndex === nextDays.length - 1}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </Row>
            ) : (
              <Row className="justify-content-center py-4">
                <Col xs="12" className="text-center">
                  <img
                    src={calendarIcon}
                    alt="Calendar"
                    style={{ width: "50px", height: "50px" }}
                  />
                  <h3>No Timings found!</h3>
                </Col>
              </Row>
            )}
          </ModalBody>
        </Modal>
      </div>

      <Modal
        isOpen={noSlotAvailable && isOpen && !isLoading}
        toggle={() => setNoSlotAvailable(false)}
        centered
      >
        <ModalHeader toggle={() => setNoSlotAvailable(false)}>
          No Slots Available
        </ModalHeader>
        <ModalBody className="text-center">
          <p>
            No slots are available for this date. Please select another day.
          </p>
          <Button color="primary" onClick={() => setNoSlotAvailable(false)}>
            OK
          </Button>
        </ModalBody>
      </Modal>
    </>
  )
}

export default DoctorTimings
